import { useState, useMemo } from "react";
import PageHeader from "@/components/PageHeader";
import { AFFILIATE_PRODUCTS } from "@/data/content";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowUpRight, Search as SearchIcon, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Affiliate() {
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const categories = ["All", ...Array.from(new Set(AFFILIATE_PRODUCTS.map((p) => p.category)))];
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        return AFFILIATE_PRODUCTS.filter((p) => {
            const okCat = cat === "All" || p.category === cat;
            const okQ = !s || p.name.toLowerCase().includes(s) || p.blurb.toLowerCase().includes(s);
            return okCat && okQ;
        });
    }, [q, cat]);

    return (
        <>
            <PageHeader
                eyebrow="Curated Picks"
                title="Hand-picked products we've tested and loved."
                subtitle="Every pick below was vetted in the real world. Some links are partner links — we may earn a small referral reward at no extra cost to you."
            />
            <section className="container-editorial pb-8">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="relative flex-1 max-w-md">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="affiliate-search" className="pl-9 h-11" placeholder="Search picks…" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`aff-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => setCat(c)}
                                className={`text-xs uppercase tracking-widest px-3 py-2 rounded-full border transition ${
                                    cat === c ? "bg-primary text-primary-foreground border-primary" : "bg-white border-stone-300 hover:border-primary/40"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>
            <section className="container-editorial pb-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((p) => (
                    <a
                        key={p.slug}
                        id={p.slug}
                        href={p.link}
                        data-testid={`aff-product-${p.slug}`}
                        rel="sponsored noopener"
                        className="card-editorial rounded-lg overflow-hidden flex flex-col"
                    >
                        <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                            <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-5 flex-1 flex flex-col">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-widest text-stone-500">{p.category}</span>
                                {p.tag && <Badge className="bg-accent text-accent-foreground border-0 hover:bg-accent">{p.tag}</Badge>}
                            </div>
                            <h3 className="font-serif text-xl mt-3">{p.name}</h3>
                            <p className="text-stone-600 text-sm mt-2 flex-1">{p.blurb}</p>
                            <div className="mt-4 flex items-center justify-between">
                                <span className="text-primary font-semibold">{p.price}</span>
                                <span className="text-primary text-sm font-semibold inline-flex items-center gap-1">
                                    Visit <ArrowUpRight className="h-4 w-4" />
                                </span>
                            </div>
                        </div>
                    </a>
                ))}
            </section>
            <section className="container-editorial pb-24">
                <div className="bg-stone-100 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start gap-6">
                    <ShieldCheck className="h-8 w-8 text-primary shrink-0" />
                    <div className="flex-1">
                        <h3 className="font-serif text-2xl">A note on trust</h3>
                        <p className="text-stone-600 mt-2">
                            We only recommend products we've paid for or extensively tested. Read our{" "}
                            <Link to="/affiliate-disclosure" className="underline text-primary">Partners & Referral Disclosure</Link>.
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
